/**
 * Sample Data Manager
 * 
 * Provides sample JSON data for testing the JSON Viewer
 * Includes both short and longer examples for comprehensive testing
 */

export class SampleDataManager {
  constructor() {
    this.shortSamples = [
      {
        name: "Simple User",
        data: {
          "id": 1,
          "name": "John Doe",
          "email": "john@example.com",
          "active": true
        }
      },
      {
        name: "Product Info",
        data: {
          "product": "JSON Viewer",
          "version": "1.0.0",
          "price": 0,
          "features": ["validation", "beautification", "search"]
        }
      },
      {
        name: "Simple Config",
        data: {
          "theme": "light",
          "autoSave": true,
          "notifications": {
            "email": true,
            "push": false
          }
        }
      },
      {
        name: "API Response",
        data: {
          "status": "success",
          "code": 200,
          "data": {
            "message": "Operation completed successfully"
          },
          "timestamp": "2025-01-08T19:35:00Z"
        }
      }
    ]

    this.longSamples = [
      {
        name: "User Profile",
        data: {
          "user": {
            "id": 12345,
            "username": "johndoe123",
            "profile": {
              "firstName": "John",
              "lastName": "Doe",
              "email": "john.doe@example.com",
              "phone": "+1-555-123-4567",
              "avatar": "https://example.com/avatars/johndoe.jpg",
              "bio": "Software developer passionate about creating amazing user experiences",
              "location": {
                "city": "San Francisco",
                "state": "California",
                "country": "USA",
                "timezone": "America/Los_Angeles"
              }
            },
            "preferences": {
              "theme": "dark",
              "language": "en-US",
              "notifications": {
                "email": true,
                "push": true,
                "sms": false,
                "newsletter": true
              },
              "privacy": {
                "profileVisible": true,
                "showEmail": false,
                "showPhone": false
              }
            },
            "subscription": {
              "plan": "premium",
              "status": "active",
              "startDate": "2024-01-15T00:00:00Z",
              "endDate": "2025-01-15T00:00:00Z",
              "autoRenew": true,
              "paymentMethod": {
                "type": "credit_card",
                "last4": "1234",
                "expiryMonth": 12,
                "expiryYear": 2026
              }
            },
            "activity": {
              "lastLogin": "2025-01-08T18:30:00Z",
              "loginCount": 1247,
              "accountCreated": "2023-06-10T14:22:00Z",
              "emailVerified": true,
              "phoneVerified": false
            }
          }
        }
      },
      {
        name: "E-commerce Order",
        data: {
          "order": {
            "id": "ORD-2025-001234",
            "number": "2025001234",
            "status": "processing",
            "customer": {
              "id": 67890,
              "name": "Jane Smith",
              "email": "jane.smith@example.com",
              "phone": "+1-555-987-6543"
            },
            "items": [
              {
                "id": "PROD-001",
                "name": "Wireless Headphones",
                "sku": "WH-1000XM4",
                "quantity": 1,
                "price": 299.99,
                "discount": 30.00,
                "finalPrice": 269.99,
                "attributes": {
                  "color": "Black",
                  "size": "One Size",
                  "warranty": "2 years"
                }
              },
              {
                "id": "PROD-002",
                "name": "USB-C Cable",
                "sku": "USB-C-2M",
                "quantity": 2,
                "price": 19.99,
                "discount": 0,
                "finalPrice": 19.99,
                "attributes": {
                  "length": "2 meters",
                  "type": "USB-C to USB-C"
                }
              }
            ],
            "pricing": {
              "subtotal": 339.97,
              "discount": 30.00,
              "tax": 24.70,
              "shipping": 9.99,
              "total": 344.66
            },
            "shipping": {
              "method": "standard",
              "carrier": "UPS",
              "trackingNumber": "1Z999AA1234567890",
              "address": {
                "street": "123 Main Street",
                "unit": "Apt 4B",
                "city": "New York",
                "state": "NY",
                "zipCode": "10001",
                "country": "USA"
              },
              "estimatedDelivery": "2025-01-12T17:00:00Z"
            },
            "payment": {
              "method": "credit_card",
              "status": "paid",
              "transactionId": "TXN-ABC123DEF456",
              "paidAt": "2025-01-08T15:45:00Z",
              "card": {
                "brand": "Visa",
                "last4": "4242",
                "expiryMonth": 8,
                "expiryYear": 2027
              }
            },
            "timestamps": {
              "createdAt": "2025-01-08T15:30:00Z",
              "updatedAt": "2025-01-08T16:00:00Z",
              "shippedAt": null,
              "deliveredAt": null
            },
            "metadata": {
              "source": "web",
              "campaign": "winter_sale_2025",
              "referrer": "https://google.com",
              "userAgent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
            }
          }
        }
      },
      {
        name: "API Configuration",
        data: {
          "application": {
            "name": "JSON Viewer API",
            "version": "2.1.0",
            "environment": "production",
            "debug": false,
            "server": {
              "host": "api.jsonviewer.com",
              "port": 443,
              "protocol": "https",
              "timeout": 30000,
              "maxConnections": 1000,
              "keepAlive": true
            },
            "database": {
              "primary": {
                "type": "postgresql",
                "host": "db-primary.internal",
                "port": 5432,
                "database": "jsonviewer_prod",
                "username": "api_user",
                "ssl": true,
                "maxConnections": 50,
                "connectionTimeout": 10000,
                "queryTimeout": 30000
              },
              "replica": {
                "type": "postgresql",
                "host": "db-replica.internal",
                "port": 5432,
                "database": "jsonviewer_prod",
                "username": "readonly_user",
                "ssl": true,
                "maxConnections": 20,
                "connectionTimeout": 10000,
                "queryTimeout": 30000
              }
            },
            "redis": {
              "host": "redis-cluster.internal",
              "port": 6379,
              "password": "encrypted_password_here",
              "database": 0,
              "maxConnections": 100,
              "ttl": 3600
            },
            "features": {
              "jsonValidation": {
                "enabled": true,
                "maxSize": "10MB",
                "timeout": 5000
              },
              "jsonBeautification": {
                "enabled": true,
                "maxSize": "5MB",
                "indentation": 2
              },
              "jsonSharing": {
                "enabled": true,
                "maxSize": "1MB",
                "expirationDays": 30,
                "rateLimiting": {
                  "enabled": true,
                  "maxRequestsPerHour": 100,
                  "maxRequestsPerDay": 1000
                }
              },
              "analytics": {
                "enabled": true,
                "provider": "google_analytics",
                "trackingId": "GA-XXXXXXXXX-X",
                "anonymizeIp": true
              }
            },
            "security": {
              "cors": {
                "enabled": true,
                "allowedOrigins": [
                  "https://jsonviewer.com",
                  "https://www.jsonviewer.com",
                  "https://app.jsonviewer.com"
                ],
                "allowedMethods": ["GET", "POST", "PUT", "DELETE"],
                "allowedHeaders": ["Content-Type", "Authorization", "X-API-Key"]
              },
              "rateLimit": {
                "enabled": true,
                "windowMs": 900000,
                "maxRequests": 1000,
                "skipSuccessfulRequests": false
              },
              "authentication": {
                "required": false,
                "apiKeys": {
                  "enabled": true,
                  "headerName": "X-API-Key"
                }
              }
            },
            "monitoring": {
              "healthCheck": {
                "enabled": true,
                "endpoint": "/health",
                "interval": 30
              },
              "metrics": {
                "enabled": true,
                "provider": "prometheus",
                "endpoint": "/metrics"
              },
              "logging": {
                "level": "info",
                "format": "json",
                "outputs": ["console", "file"],
                "maxFileSize": "100MB",
                "maxFiles": 10
              }
            }
          }
        }
      }
    ]
  }

  /**
   * Get a random sample (either short or long)
   */
  getRandomSample() {
    const useShort = Math.random() < 0.6 // 60% chance for short samples
    const samples = useShort ? this.shortSamples : this.longSamples
    const randomIndex = Math.floor(Math.random() * samples.length)
    const sample = samples[randomIndex]

    return {
      name: sample.name,
      data: JSON.stringify(sample.data, null, 2),
      type: useShort ? 'short' : 'long'
    }
  }

  /**
   * Get a random short sample
   */
  getRandomShortSample() {
    const randomIndex = Math.floor(Math.random() * this.shortSamples.length)
    const sample = this.shortSamples[randomIndex]

    return {
      name: sample.name,
      data: JSON.stringify(sample.data, null, 2),
      type: 'short'
    }
  }

  /**
   * Get a random long sample
   */
  getRandomLongSample() {
    const randomIndex = Math.floor(Math.random() * this.longSamples.length)
    const sample = this.longSamples[randomIndex]

    return {
      name: sample.name,
      data: JSON.stringify(sample.data, null, 2),
      type: 'long'
    }
  }

  /**
   * Get all available samples
   */
  getAllSamples() {
    return {
      short: this.shortSamples.map(sample => ({
        name: sample.name,
        data: JSON.stringify(sample.data, null, 2),
        type: 'short'
      })),
      long: this.longSamples.map(sample => ({
        name: sample.name,
        data: JSON.stringify(sample.data, null, 2),
        type: 'long'
      }))
    }
  }
}
